/**
 * What: Shared definitions for the two Study Builder draft decorator pilot operations
 *   (plan https://github.com/Yesterday-Work/ssr-validation-tool/blob/fdcf45fb4/docs/plans/2026-09-28-shopify-admin-api-parity.md, deliverable A2/A4 phase 2 pilot).
 * Transport / data-flow: Pure zod contracts + `ShopifyOperationDefinition` metadata, imported by
 *   both the Shopify Worker (handler + merchant/operator dispatch) and Web (generated Admin
 *   action descriptors + signed transport). No D1, Hono, or fetch code lives here.
 */
import * as z from 'zod';
import { studyBuilderSnapshotSchema, studyBuilderWireSnapshotFields } from './shopify-study-builder-snapshot.js';
import { retainedOpportunityStudySetupSchema } from './opportunity-study-setup.js';
import { studyAuthoringNextTestSeedSchema } from './study-next-test-selection.js';
/** Provenance of a draft seeded from a saved insight. */
export const insightSetupSeedSchema = z.strictObject({
    batchId: z.string().min(1),
    taskId: z.string().min(1),
    generatedAt: z.string().min(1),
    insightId: z.string().min(1),
    productId: z.string().min(1),
    researchInputId: z.string().min(1),
    kind: z.string().min(1),
    rule: z.strictObject({ id: z.string().min(1) }),
    /** Immutable initial proposal, not a claim that later merchant edits retain its support. */
    initialProposal: retainedOpportunityStudySetupSchema.optional(),
});
/**
 * Declarative on purpose: signed admin operations fingerprint this schema with
 * `z.toJSONSchema`, which cannot represent `.transform()`.
 * Wire fields are complete; the only default kept from the base snapshot is the explicit v1 rule:
 * before product locking existed, omitted `productSelectionLocked` meant unlocked.
 * Both the browser and merchant/operator responses validate here. Server-only semantic,
 * credential, ownership and legacy Opportunity provenance rules remain in validateShopifyStudyBuilderDraft.
 */
export const shopifyStudyBuilderDraftShapeSchema = studyBuilderSnapshotSchema
    .safeExtend({
    ...studyBuilderWireSnapshotFields,
    opportunitySeed: z.unknown().optional(),
    insightSetupSeed: insightSetupSeedSchema.optional(),
    nextTestSeed: studyAuthoringNextTestSeedSchema.optional(),
})
    .strict();
/**
 * Plain explanations for why a saved draft cannot be inspected or planned. Shopify throws with
 * this text and Web shows it to operators, so the reason never reaches anyone as a bare code.
 */
export const SHOPIFY_DRAFT_READINESS_PROBLEMS = {
    DRAFT_PRODUCT_NOT_LOCKED: 'This draft was not created from an insight or opportunity, so its product can still be changed. Inspect only checks drafts created that way, where the product is fixed.',
    DRAFT_PRODUCT_MISSING: 'This draft has no product chosen yet.',
    DRAFT_PRODUCT_ID_MISMATCH: 'The product saved in this draft does not match the product it points to, so the draft is damaged.',
    DRAFT_PRODUCT_CURRENCY_MISSING: 'The product in this draft has no currency saved, so its price cannot be shown to shoppers.',
    DRAFT_VARIANT_REQUIRED: 'This product has several colours and none is chosen yet, so the test cannot be prepared.',
    DRAFT_AUDIENCE_CUSTOMER_GROUP_UNRESOLVED: 'This draft targets a customer group the store no longer has.',
    DRAFT_AUDIENCE_INVALID: 'The audience in this draft cannot be turned into a group of shoppers.',
};
export const shopifyStudyBuilderDraftReadRequestSchema = z.strictObject({
    draftId: z.string().trim().min(1).max(256),
});
export const shopifyStudyBuilderDraftReadResponseSchema = z.union([
    z.strictObject({ draft: z.null() }),
    z.strictObject({
        draftId: z.string(),
        draft: shopifyStudyBuilderDraftShapeSchema,
        updatedAt: z.string(),
    }),
]);
export const shopifyStudyBuilderDraftWriteRequestSchema = z.strictObject({
    draftId: z.string().trim().min(1).max(256),
    // Stays `unknown` on the wire: `validateShopifyStudyBuilderDraft` is the one owner of the
    // domain shape (see study-builder-draft-routes.ts for why this must not be duplicated here).
    draft: z.unknown(),
});
export const shopifyStudyBuilderDraftWriteResponseSchema = z.strictObject({
    draftId: z.string(),
    draft: shopifyStudyBuilderDraftShapeSchema,
    updatedAt: z.string(),
});
export const SHOPIFY_STUDY_BUILDER_DRAFT_READ_OPERATION = {
    id: 'study-builder-draft.read',
    method: 'GET',
    path: '/api/study-builder-draft',
    requestSchema: shopifyStudyBuilderDraftReadRequestSchema,
    responseSchema: shopifyStudyBuilderDraftReadResponseSchema,
    auth: { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] },
    // Retired merchant/operator entry points return 410 without reading legacy drafts.
    effects: [],
    risk: { cost: 'free', destructive: false, dryRunCapable: true },
    idempotent: true,
};
export const SHOPIFY_STUDY_BUILDER_DRAFT_WRITE_OPERATION = {
    id: 'study-builder-draft.write',
    method: 'PUT',
    path: '/api/study-builder-draft',
    requestSchema: shopifyStudyBuilderDraftWriteRequestSchema,
    responseSchema: shopifyStudyBuilderDraftWriteResponseSchema,
    auth: { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] },
    // Retired merchant/operator entry points refuse before source reads or persistence.
    effects: [],
    risk: { cost: 'free', destructive: false, dryRunCapable: false },
    idempotent: true,
};
//# sourceMappingURL=shopify-study-builder-draft-operation.js.map